import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Grid, Chip, Button, CircularProgress, Alert } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import useApi from '../../../hooks/useGaneshApi';

interface SchemaDiffProps {
  customerId: number;
  customerName: string;
  schema1Hash: string;
  schema2Hash: string;
  schema1Data?: any;
  schema2Data?: any;
  schema1DeliveryIds?: string[];
  schema2DeliveryIds?: string[];
  schema1DeliveryCount?: number;
  schema2DeliveryCount?: number;
}

interface SchemaField {
  attribute: string;
  label?: string;
  type?: string;
  [key: string]: any;
}

interface SchemaMetaField {
  [key: string]: any;
}

interface FieldDiff {
  type: 'added' | 'removed' | 'modified' | 'unchanged';
  attribute: string;
  schema1Field?: SchemaField;
  schema2Field?: SchemaField;
  changes?: string[];
}

interface SchemaDiffResult {
  metaDiffs: FieldDiff[];
  fieldDiffs: FieldDiff[];
}

const SchemaDiff: React.FC<SchemaDiffProps> = ({ 
  customerId, 
  customerName, 
  schema1Hash, 
  schema2Hash,
  schema1DeliveryIds = [],
  schema2DeliveryIds = [],
  schema1DeliveryCount = 0,
  schema2DeliveryCount = 0
}) => {
  const safeSchema1DeliveryIds = Array.isArray(schema1DeliveryIds) ? schema1DeliveryIds : [];
  const safeSchema2DeliveryIds = Array.isArray(schema2DeliveryIds) ? schema2DeliveryIds : [];
  const { apiRequest } = useApi();
  const [schema1, setSchema1] = useState<any>(null);
  const [schema2, setSchema2] = useState<any>(null);
  const [schema1HumanFriendlyHash, setSchema1HumanFriendlyHash] = useState<string>('');
  const [schema2HumanFriendlyHash, setSchema2HumanFriendlyHash] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchAttempted, setFetchAttempted] = useState(false);

  // Fetch actual schema data based on hashes
  useEffect(() => {
    const fetchSchemas = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await apiRequest<{ data: any[] }>(`/v1/schemas/schemas-by-hashes`, {
          method: 'POST',
          body: { schema_hashes: [schema1Hash, schema2Hash] }
        });

        if (response.data && Array.isArray(response.data) && response.data.length === 2) {
          const schemas = response.data;
          
          const schema1Data = schemas.find(s => s.encoded_schema_hash === schema1Hash);
          const schema2Data = schemas.find(s => s.encoded_schema_hash === schema2Hash);
          
          if (schema1Data?.schema && schema2Data?.schema) {
            setSchema1(schema1Data.schema);
            setSchema2(schema2Data.schema);
            setSchema1HumanFriendlyHash(schema1Data.human_friendly_encoded_schema_hash || schema1Hash);
            setSchema2HumanFriendlyHash(schema2Data.human_friendly_encoded_schema_hash || schema2Hash);
          } else {
            setError('One or both schemas not found in the response');
          }
        } else {
          setError('Invalid response: Expected exactly 2 schemas');
        }
      } catch (err) {
        console.error('Error fetching schemas:', err);
        setError(`Failed to fetch schema data: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setLoading(false);
        setFetchAttempted(true);
      }
    };

    if (schema1Hash && schema2Hash && !fetchAttempted) {
      fetchSchemas();
    } else if (!schema1Hash || !schema2Hash) {
      setError('Schema hashes are required for comparison');
      setLoading(false);
      setFetchAttempted(true);
    }
  }, [schema1Hash, schema2Hash, apiRequest, fetchAttempted]);
  
  const schemaDiff = React.useMemo((): SchemaDiffResult => {
    if (!schema1 || !schema2) {
      return { metaDiffs: [], fieldDiffs: [] };
    }

    // Compare meta sections
    const metaDiffs: FieldDiff[] = [];
    const allMetaKeys = new Set([
      ...Object.keys(schema1.meta || {}),
      ...Object.keys(schema2.meta || {})
    ]);

    allMetaKeys.forEach(key => {
      const val1 = (schema1.meta as SchemaMetaField)?.[key];
      const val2 = (schema2.meta as SchemaMetaField)?.[key];
      
      if (val1 === undefined && val2 !== undefined) {
        metaDiffs.push({
          type: 'added',
          attribute: key,
          schema2Field: val2
        });
      } else if (val1 !== undefined && val2 === undefined) {
        metaDiffs.push({
          type: 'removed', 
          attribute: key,
          schema1Field: val1
        });
      } else if (val1 !== val2) {
        metaDiffs.push({
          type: 'modified',
          attribute: key,
          schema1Field: val1,
          schema2Field: val2,
          changes: [`${val1} → ${val2}`]
        });
      }
    });

    // Compare fields sections
    const fieldDiffs: FieldDiff[] = [];
    const schema1Fields = schema1.fields as SchemaField[];
    const schema2Fields = schema2.fields as SchemaField[];
    
    // Create maps for both attribute and label matching
    const schema1ByAttribute = new Map(schema1Fields.map(f => [f.attribute, f]));
    const schema2ByAttribute = new Map(schema2Fields.map(f => [f.attribute, f]));
    const schema1ByLabel = new Map(schema1Fields.filter(f => f.label).map(f => [f.label!, f]));
    const schema2ByLabel = new Map(schema2Fields.filter(f => f.label).map(f => [f.label!, f]));
    
    const processedFields = new Set<string>();
    
    // First pass: exact attribute matches
    const allFieldAttributes = new Set([...schema1ByAttribute.keys(), ...schema2ByAttribute.keys()]);
    
    allFieldAttributes.forEach(attribute => {
      const field1 = schema1ByAttribute.get(attribute);
      const field2 = schema2ByAttribute.get(attribute);
      
      if (field1 && field2) {
        // Both fields exist with same attribute
        processedFields.add(field1.attribute);
        processedFields.add(field2.attribute);
        
        // Check for changes in the field
        const changes: string[] = [];
        
        if (field1.label !== field2.label) {
          changes.push(`label: "${field1.label || 'none'}" → "${field2.label || 'none'}"`);
        }
        
        if (field1.type !== field2.type) {
          changes.push(`type: "${field1.type || 'none'}" → "${field2.type || 'none'}"`);
        }
        
        // Check other properties
        const allProps = new Set([...Object.keys(field1), ...Object.keys(field2)]);
        allProps.forEach(prop => {
          if (prop !== 'label' && prop !== 'type' && prop !== 'attribute') {
            const val1 = (field1 as any)[prop];
            const val2 = (field2 as any)[prop];
            if (val1 !== val2) {
              changes.push(`${prop}: "${val1 || 'none'}" → "${val2 || 'none'}"`);
            }
          }
        });
        
        if (changes.length > 0) {
          fieldDiffs.push({
            type: 'modified',
            attribute: `${field1.attribute} / ${field2.attribute}`,
            schema1Field: field1,
            schema2Field: field2,
            changes
          });
        }
      }
    });
    
    // Second pass: label-based matching for unmatched fields
    const unprocessedSchema1 = schema1Fields.filter(f => !processedFields.has(f.attribute));
    const unprocessedSchema2 = schema2Fields.filter(f => !processedFields.has(f.attribute));
    
    unprocessedSchema1.forEach(field1 => {
      if (field1.label) {
        const field2 = unprocessedSchema2.find(f => f.label === field1.label && !processedFields.has(f.attribute));
        if (field2) {
          // Found a match by label
          processedFields.add(field1.attribute);
          processedFields.add(field2.attribute);
          
          const changes: string[] = [];
          changes.push(`attribute: "${field1.attribute}" → "${field2.attribute}"`);
          
          if (field1.type !== field2.type) {
            changes.push(`type: "${field1.type || 'none'}" → "${field2.type || 'none'}"`);
          }
          
          // Check other properties
          const allProps = new Set([...Object.keys(field1), ...Object.keys(field2)]);
          allProps.forEach(prop => {
            if (prop !== 'label' && prop !== 'type' && prop !== 'attribute') {
              const val1 = (field1 as any)[prop];
              const val2 = (field2 as any)[prop];
              if (val1 !== val2) {
                changes.push(`${prop}: "${val1 || 'none'}" → "${val2 || 'none'}"`);
              }
            }
          });
          
          fieldDiffs.push({
            type: 'modified',
            attribute: field1.label, // Use the shared label as the key
            schema1Field: field1,
            schema2Field: field2,
            changes
          });
        }
      }
    });
    
    // Third pass: label-to-attribute matching
    const stillUnprocessedSchema1 = unprocessedSchema1.filter(f => !processedFields.has(f.attribute));
    const stillUnprocessedSchema2 = unprocessedSchema2.filter(f => !processedFields.has(f.attribute));
    
    stillUnprocessedSchema1.forEach(field1 => {
      if (field1.label) {
        // Try to find a field in schema2 where the attribute matches this field's label
        const field2 = stillUnprocessedSchema2.find(f => 
          f.attribute === field1.label && !processedFields.has(f.attribute)
        );
        if (field2) {
          processedFields.add(field1.attribute);
          processedFields.add(field2.attribute);
          
          const changes: string[] = [];
          changes.push(`attribute: "${field1.attribute}" → "${field2.attribute}"`);
          changes.push(`label: "${field1.label}" → "none"`);
          
          if (field1.type !== field2.type) {
            changes.push(`type: "${field1.type || 'none'}" → "${field2.type || 'none'}"`);
          }
          
          fieldDiffs.push({
            type: 'modified',
            attribute: field1.label, // Use the label as the key
            schema1Field: field1,
            schema2Field: field2,
            changes
          });
        }
      }
    });
    
    // Fourth pass: attribute-to-label matching
    const finalUnprocessedSchema2 = stillUnprocessedSchema2.filter(f => !processedFields.has(f.attribute));
    
    finalUnprocessedSchema2.forEach(field2 => {
      if (field2.label) {
        // Try to find a field in schema1 where the attribute matches this field's label
        const field1 = stillUnprocessedSchema1.find(f => 
          f.attribute === field2.label && !processedFields.has(f.attribute)
        );
        if (field1) {
          processedFields.add(field1.attribute);
          processedFields.add(field2.attribute);
          
          const changes: string[] = [];
          changes.push(`attribute: "${field1.attribute}" → "${field2.attribute}"`);
          changes.push(`label: "none" → "${field2.label}"`);
          
          if (field1.type !== field2.type) {
            changes.push(`type: "${field1.type || 'none'}" → "${field2.type || 'none'}"`);
          }
          
          fieldDiffs.push({
            type: 'modified',
            attribute: field2.label, // Use the label as the key
            schema1Field: field1,
            schema2Field: field2,
            changes
          });
        }
      }
    });
    
    // Fifth pass: remaining unmatched fields (added/removed)
    schema1Fields.forEach(field1 => {
      if (!processedFields.has(field1.attribute)) {
        fieldDiffs.push({
          type: 'removed',
          attribute: field1.attribute,
          schema1Field: field1
        });
      }
    });
    
    schema2Fields.forEach(field2 => {
      if (!processedFields.has(field2.attribute)) {
        fieldDiffs.push({
          type: 'added',
          attribute: field2.attribute,
          schema2Field: field2
        });
      }
    });

    return { metaDiffs, fieldDiffs };
  }, [schema1, schema2]);

  const renderMetaSideBySide = (diff: FieldDiff) => {
    const renderMetaContent = (value: any, side: 'left' | 'right') => {
      if (value === undefined) {
        return (
          <Box sx={{ 
            padding: '8px 12px', 
            color: 'text.disabled',
            fontStyle: 'italic',
            fontFamily: 'monospace',
            fontSize: '13px'
          }}>
            (not present)
          </Box>
        );
      }

      const shouldHighlightValue = diff.type === 'modified';
      const getHighlightColor = () => {
        if (diff.type === 'added') return '#e6ffed';
        if (diff.type === 'removed') return '#ffeef0';
        if (shouldHighlightValue) return '#fff3cd'; // Yellow for modified values
        return 'transparent';
      };

      return (
        <Box sx={{ 
          padding: '8px 12px',
          fontFamily: 'monospace',
          fontSize: '13px'
        }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
            attribute: {diff.attribute}
          </Typography>
          
          <Box sx={{ 
            ml: 1, 
            color: 'text.secondary',
            backgroundColor: getHighlightColor(),
            padding: '2px 4px',
            borderRadius: '2px',
            display: 'inline-block',
            mt: 0.5
          }}>
            value: {JSON.stringify(value)}
          </Box>
        </Box>
      );
    };

    const getBorderColor = () => {
      switch (diff.type) {
        case 'added': return '#28a745';
        case 'removed': return '#dc3545';
        case 'modified': return '#ffc107';
        default: return '#e0e0e0';
      }
    };

    const getIcon = () => {
      switch (diff.type) {
        case 'added': return '+ ';
        case 'removed': return '- ';
        case 'modified': return '~ ';
        default: return '  ';
      }
    };

    return (
      <Box
        key={diff.attribute}
        sx={{
          display: 'flex',
          border: `1px solid ${getBorderColor()}`,
          borderRadius: '4px',
          margin: '2px 0',
          overflow: 'hidden'
        }}
      >
        {/* Left Side - Schema 1 */}
        <Box sx={{ 
          flex: 1, 
          backgroundColor: diff.type === 'removed' ? '#ffeef0' : 'transparent',
          borderRight: `1px solid ${getBorderColor()}`
        }}>
          {renderMetaContent(diff.schema1Field, 'left')}
        </Box>

        {/* Change Indicator */}
        <Box sx={{
          width: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.100',
          fontFamily: 'monospace',
          fontWeight: 'bold'
        }}>
          {getIcon().trim()}
        </Box>

        {/* Right Side - Schema 2 */}
        <Box sx={{ 
          flex: 1, 
          backgroundColor: diff.type === 'added' ? '#e6ffed' : 'transparent',
          borderLeft: `1px solid ${getBorderColor()}`
        }}>
          {renderMetaContent(diff.schema2Field, 'right')}
        </Box>
      </Box>
    );
  };

  const renderFieldSideBySide = (diff: FieldDiff) => {
    const renderFieldContent = (field: SchemaField | undefined, side: 'left' | 'right') => {
      if (!field) {
        return (
          <Box sx={{ 
            padding: '8px 12px', 
            color: 'text.disabled',
            fontStyle: 'italic',
            fontFamily: 'monospace',
            fontSize: '13px'
          }}>
            (not present)
          </Box>
        );
      }

      // Determine what should be highlighted based on the diff type and changes
      const shouldHighlightAttribute = () => {
        if (diff.type !== 'modified' || !diff.changes) return false;
        return diff.changes.some(change => change.startsWith('attribute:'));
      };

      const shouldHighlightLabel = () => {
        if (diff.type !== 'modified' || !diff.changes) return false;
        return diff.changes.some(change => change.startsWith('label:'));
      };

      const getHighlightColor = (isAttribute: boolean, isLabel: boolean) => {
        if (diff.type === 'added') return '#e6ffed';
        if (diff.type === 'removed') return '#ffeef0';
        if (diff.type === 'modified') {
          if (isAttribute && shouldHighlightAttribute()) {
            return side === 'left' ? '#ffeef0' : '#e6ffed'; // Red for left, green for right
          }
          if (isLabel && shouldHighlightLabel()) {
            return side === 'left' ? '#ffeef0' : '#e6ffed'; // Red for left, green for right
          }
          // For complex cases like location_id -> store_number, highlight everything
          if (diff.changes?.some(c => c.includes('→')) && 
              (shouldHighlightAttribute() || shouldHighlightLabel())) {
            return side === 'left' ? '#ffeef0' : '#e6ffed';
          }
        }
        return 'transparent';
      };

      return (
        <Box sx={{ 
          padding: '8px 12px',
          fontFamily: 'monospace',
          fontSize: '13px'
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 'bold', 
              fontFamily: 'monospace',
              backgroundColor: getHighlightColor(true, false),
              padding: '2px 4px',
              borderRadius: '2px',
              display: 'inline-block'
            }}
          >
            attribute: {field.attribute}
          </Typography>
          
          <Box sx={{ 
            ml: 1, 
            color: 'text.secondary',
            backgroundColor: getHighlightColor(false, true),
            padding: '2px 4px',
            borderRadius: '2px',
            display: 'inline-block',
            mt: 0.5
          }}>
            label: {field.label ? `"${field.label}"` : 'empty'}
          </Box>
          
          {field.type && (
            <Box sx={{ ml: 1, color: 'text.secondary', mt: 0.5 }}>
              type: "{field.type}"
            </Box>
          )}
        </Box>
      );
    };

    const getBackgroundColor = (side: 'left' | 'right') => {
      if (diff.type === 'added') return side === 'right' ? '#e6ffed' : 'transparent';
      if (diff.type === 'removed') return side === 'left' ? '#ffeef0' : 'transparent';
      return 'transparent';
    };

    const getBorderColor = () => {
      switch (diff.type) {
        case 'added': return '#28a745';
        case 'removed': return '#dc3545';
        case 'modified': return '#ffc107';
        default: return '#e0e0e0';
      }
    };

    const getIcon = () => {
      switch (diff.type) {
        case 'added': return '+ ';
        case 'removed': return '- ';
        case 'modified': return '~ ';
        default: return '  ';
      }
    };

    return (
      <Box
        key={diff.attribute}
        sx={{
          display: 'flex',
          border: `1px solid ${getBorderColor()}`,
          borderRadius: '4px',
          margin: '2px 0',
          overflow: 'hidden'
        }}
      >
        {/* Left Side - Schema 1 */}
        <Box sx={{ 
          flex: 1, 
          backgroundColor: getBackgroundColor('left'),
          borderRight: `1px solid ${getBorderColor()}`
        }}>
          {renderFieldContent(diff.schema1Field, 'left')}
        </Box>

        {/* Change Indicator */}
        <Box sx={{
          width: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.100',
          fontFamily: 'monospace',
          fontWeight: 'bold'
        }}>
          {getIcon().trim()}
        </Box>

        {/* Right Side - Schema 2 */}
        <Box sx={{ 
          flex: 1, 
          backgroundColor: getBackgroundColor('right'),
          borderLeft: `1px solid ${getBorderColor()}`
        }}>
          {renderFieldContent(diff.schema2Field, 'right')}
        </Box>
      </Box>
    );
  };

  const allDiffs = [...schemaDiff.metaDiffs, ...schemaDiff.fieldDiffs];
  
  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading schema data...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!schema1 || !schema2) {
  return (
    <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load schema data. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">
          Schema Comparison - {customerName}
      </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Column Headers */}
          <Box sx={{ 
            display: 'flex', 
            mb: 2,
            border: '2px solid #1976d2',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <Box sx={{ 
              flex: 1, 
              p: 2, 
              backgroundColor: '#e3f2fd',
              borderRight: '1px solid #1976d2'
            }}>
              <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                Schema 1
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                Hash: {schema1HumanFriendlyHash || schema1Hash}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Deliveries: {schema1DeliveryCount}
              </Typography>
              {safeSchema1DeliveryIds.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    Delivery IDs:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {safeSchema1DeliveryIds.slice(0, 5).map((id) => (
                      <Chip key={id} label={id} size="small" variant="outlined" />
                    ))}
                    {safeSchema1DeliveryIds.length > 5 && (
                      <Chip label={`+${safeSchema1DeliveryIds.length - 5} more`} size="small" />
                    )}
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={{ 
              width: '40px', 
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Δ
              </Typography>
            </Box>
            <Box sx={{ 
              flex: 1, 
              p: 2, 
              backgroundColor: '#e8f5e8',
              borderLeft: '1px solid #1976d2'
            }}>
              <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>
                Schema 2
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                Hash: {schema2HumanFriendlyHash || schema2Hash}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Deliveries: {schema2DeliveryCount}
              </Typography>
              {safeSchema2DeliveryIds.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    Delivery IDs:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {safeSchema2DeliveryIds.slice(0, 5).map((id) => (
                      <Chip key={id} label={id} size="small" variant="outlined" />
                    ))}
                    {safeSchema2DeliveryIds.length > 5 && (
                      <Chip label={`+${safeSchema2DeliveryIds.length - 5} more`} size="small" />
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          {/* Meta Differences */}
          {schemaDiff.metaDiffs.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Meta Changes ({schemaDiff.metaDiffs.length})
              </Typography>
              <Paper sx={{ p: 2 }}>
                {schemaDiff.metaDiffs.map(renderMetaSideBySide)}
              </Paper>
            </Box>
          )}

          {/* Field Differences */}
          {schemaDiff.fieldDiffs.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Field Changes ({schemaDiff.fieldDiffs.length})
              </Typography>
              <Paper sx={{ p: 2, maxHeight: '600px', overflow: 'auto' }}>
                {schemaDiff.fieldDiffs.map(renderFieldSideBySide)}
              </Paper>
            </Box>
          )}

      {/* Summary Info */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Summary:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              Meta changes: {schemaDiff.metaDiffs.length}
            </Typography>
            <Typography variant="body2">
              Field changes: {schemaDiff.fieldDiffs.length}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">
              <span style={{ color: '#28a745' }}>+ Added:</span> {schemaDiff.fieldDiffs.filter(d => d.type === 'added').length}
            </Typography>
            <Typography variant="body2">
              <span style={{ color: '#dc3545' }}>- Removed:</span> {schemaDiff.fieldDiffs.filter(d => d.type === 'removed').length}
            </Typography>
            <Typography variant="body2">
              <span style={{ color: '#ffc107' }}>~ Modified:</span> {schemaDiff.fieldDiffs.filter(d => d.type === 'modified').length}
            </Typography>
          </Grid>
        </Grid>
        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
          Side-by-side view with data model comparison
        </Typography>
      </Box>
    </Box>
  );
};

export default SchemaDiff;
